import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { HelpCircle, Send, Loader2, CheckCircle } from "lucide-react";

const AgentQuestionCard = ({ question, userId, onAnswered, questionId }) => {
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [answered, setAnswered] = useState(false);

  const handleSubmit = async () => {
    if (!answer.trim()) {
      toast.error("Please provide an answer");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/agent/answer-question`, {
        userId,
        questionId,
        question: question.text,
        answer: answer.trim(),
        context: question.context
      });

      setAnswered(true);
      toast.success("Answer submitted! Agent will use this information.");
      
      if (onAnswered) {
        onAnswered(questionId, answer);
      }
    } catch (error) {
      console.error("Error submitting answer:", error);
      toast.error("Failed to submit answer");
    } finally {
      setSubmitting(false);
    }
  };

  if (answered) {
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-400">
          <CheckCircle className="h-5 w-5" />
          <span className="font-medium">Answer submitted</span>
        </div>
        <p className="text-gray-400 text-sm mt-1">The agent will incorporate your response.</p>
      </div>
    );
  }

  return (
    <div 
      className="bg-purple-500/10 border-l-4 border-purple-500 rounded-r-lg p-4"
      data-testid="agent-question-card"
    >
      {/* Question Header */}
      <div className="flex items-center gap-2 mb-3">
        <HelpCircle className="h-5 w-5 text-purple-400" />
        <span className="text-xs font-semibold uppercase tracking-wide text-purple-400">
          Agent Needs Your Input
        </span>
      </div>

      {/* Question Text */}
      <p className="text-white text-sm mb-4 leading-relaxed">
        {question.text}
      </p>

      {/* Context hint if available */}
      {question.hint && (
        <p className="text-gray-500 text-xs mb-3 italic">
          💡 {question.hint}
        </p>
      )}

      {/* Answer Input */}
      <div className="space-y-3">
        {question.type === "long" ? (
          <Textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Type your answer..."
            className="bg-white/5 border-gray-700 text-white min-h-[80px] text-sm"
            data-testid="question-answer-textarea"
          />
        ) : (
          <Input
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Type your answer..."
            className="bg-white/5 border-gray-700 text-white text-sm"
            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            data-testid="question-answer-input"
          />
        )}

        {/* Quick Options if provided */}
        {question.options && question.options.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {question.options.map((option, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={() => setAnswer(option)}
                className={`text-xs border-gray-700 ${
                  answer === option 
                    ? 'bg-purple-500/20 border-purple-500 text-purple-300' 
                    : 'text-gray-400 hover:text-white hover:border-gray-500'
                }`}
              >
                {option}
              </Button>
            ))}
          </div>
        )}

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || !answer.trim()}
          className="bg-purple-600 hover:bg-purple-700 text-white text-sm w-full"
          data-testid="submit-answer-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Submit Answer
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AgentQuestionCard;
